// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { ClevertapAnalyticsType, DatacenterOption, IdentityOptionType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Switch, Grid, Typography, TextField } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig, verifyAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialClevertapAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { Identity, clevertapRegionOption } from 'src/assets/SettingsConfig'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'

interface ClevertapProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const Clevertap: React.FC<ClevertapProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { clevertap } = integrations || {}

  //Local State
  const [clevertapAnalytics, setClevertapAnalytics] = useState<ClevertapAnalyticsType>(initialClevertapAnalyticsState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setClevertapAnalytics(temporary.integrations?.clevertap)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(clevertap) !== JSON.stringify(clevertapAnalytics)))
  }, [clevertap, clevertapAnalytics])

  useEffect(() => {
    if (!clevertap) return
    setClevertapAnalytics(clevertap)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [clevertap, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            clevertap: clevertapAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (
      clevertapAnalytics?.enabled &&
      !Boolean(clevertapAnalytics?.accountId?.length || !Boolean(clevertapAnalytics?.passcode?.length))
    ) {
      setOpenAlert('error')
      setAlertMessage('Please fill required clevertap analytics fields!')
      return false
    }
    return true
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    const payload = {
      enabled: clevertapAnalytics?.enabled,
      account_id: clevertapAnalytics?.accountId?.trim(),
      passcode: clevertapAnalytics?.passcode?.trim(),
      region: clevertapAnalytics?.region,
      identity: clevertapAnalytics?.identity,
      channels: getEnabledChannelsArray(clevertapAnalytics?.channels)
    }
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const verifyResponse: any = await verifyAnalyticsConfig('clevertap', payload)
      if (verifyResponse?.error) {
        setOpenAlert('error')
        setAlertMessage(verifyResponse?.error)
        console.error('x-trace-id:', verifyResponse.headers?.['x-trace-id'])
        setLoaderProperties({ isLoaderVisible: false })
        return
      }

      const response: any = await setAnalyticsConfig('clevertap', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, clevertap: clevertapAnalytics }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!clevertap) return
    setClevertapAnalytics(clevertap)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <IntegrationChannels
        channels={clevertapAnalytics.channels ?? []}
        onChange={(channels: any) => {
          setClevertapAnalytics({ ...clevertapAnalytics, channels: channels })
        }}
        hideChannels={!clevertapAnalytics?.enabled}
      >
        <Grid display={'flex'} flexDirection={'column'} width={'100%'}>
          <Grid item xs={12} md={12} margin={'1rem 0rem'} display={'flex'} flexDirection={'column'} gap={2}>
            <Grid display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                Account ID:
              </Typography>
              <TextField
                fullWidth
                type='password'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '360px' }}
                value={clevertapAnalytics?.accountId}
                placeholder='Account ID'
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setClevertapAnalytics({ ...clevertapAnalytics, accountId: event?.target?.value })
                }
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                Passcode:
              </Typography>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '360px' }}
                value={clevertapAnalytics?.passcode}
                inputProps={{ 'aria-label': 'controlled' }}
                placeholder='Passcode'
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setClevertapAnalytics({ ...clevertapAnalytics, passcode: event?.target?.value })
                }
              />
            </Grid>
            <Grid display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant='body2'>Region:</Typography>
              </div>
              <div className='w-[360px]'>
                <DropdownButton
                  options={clevertapRegionOption}
                  handleClick={(index: number) =>
                    setClevertapAnalytics({
                      ...clevertapAnalytics,
                      region: clevertapRegionOption?.[index]?.value
                    })
                  }
                  variant='outlined'
                  title={'Region'}
                  selectedOption={clevertapRegionOption?.findIndex(
                    (option: DatacenterOption, index: number) => option?.value === clevertapAnalytics?.region
                  )}
                  size='large'
                />
              </div>
            </Grid>
            <Grid display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant='body2'>Identity:</Typography>
              </div>
              <div className='w-[360px]'>
                <DropdownButton
                  options={Identity}
                  handleClick={(index: number) =>
                    setClevertapAnalytics({
                      ...clevertapAnalytics,
                      identity: Identity?.[index]?.value
                    })
                  }
                  variant='outlined'
                  title={'Identity'}
                  selectedOption={Identity?.findIndex(
                    (option: IdentityOptionType, index: number) => option?.value === clevertapAnalytics?.identity
                  )}
                  size='large'
                />
              </div>
            </Grid>
          </Grid>

          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <Grid container>
              <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
                <Typography variant='body2'>Clevertap analytics is</Typography>
                <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                  {`${clevertapAnalytics?.enabled ? 'enabled' : 'disabled'}`}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
                <Switch
                  name='clevertapToggle'
                  checked={clevertapAnalytics?.enabled}
                  inputProps={{ 'aria-label': 'controlled' }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setClevertapAnalytics({ ...clevertapAnalytics, enabled: event.target.checked })
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </IntegrationChannels>

      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default Clevertap
